/**
 * PermissionRoute.js
 * 
 * A route wrapper that checks if the user has the required permission(s).
 * If not, redirects to dashboard with a toast notification.
 * 
 * Usage:
 *   <PermissionRoute permission="scheduling.shift.view_all">
 *     <ScheduleBoard />
 *   </PermissionRoute>
 * 
 *   <PermissionRoute permissions={["scheduling.shift.view_all", "timesheet.view_all"]} requireAll={false}>
 *     <SomePage />
 *   </PermissionRoute>
 */

import { Navigate } from 'react-router-dom';
import { usePermissions } from '@/contexts/PermissionContext';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useRef } from 'react';

const PermissionRoute = ({ 
  children, 
  permission,           // Single permission to check
  permissions,          // Array of permissions to check
  requireAll = false,   // If true, ALL permissions required; if false, ANY permission grants access
  redirectTo = '/dashboard',
  showToast = true
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin, loading } = usePermissions();
  const { toast } = useToast();
  const hasShownToast = useRef(false);

  // Determine if user has access
  let hasAccess = false;
  
  // Admins always have access
  if (isAdmin) {
    hasAccess = true;
  } else if (permission) {
    // Single permission check
    hasAccess = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    // Multiple permissions check
    hasAccess = requireAll 
      ? hasAllPermissions(...permissions)
      : hasAnyPermission(...permissions);
  }

  // Show toast on access denied (only once)
  useEffect(() => {
    if (!loading && !hasAccess && showToast && !hasShownToast.current) {
      hasShownToast.current = true;
      toast({
        title: "Access Denied",
        description: "You don't have permission to access this page.",
        variant: "destructive"
      });
    }
  }, [loading, hasAccess, showToast, toast]);

  // Show loading state while permissions are being fetched
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect if no access
  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  // Render children if access granted
  return children;
};

export default PermissionRoute;
